import { useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useLang } from '../i18n'
import { useConnectivity } from '../lib/connectivity'
import { isSessionExpired, subscribeSessionExpired } from '../lib/auth'
import { signInAgain } from '../lib/session-token'

/**
 * The app's one connection banner: "Reconnecting…" while the backend cannot be reached
 * (lib/connectivity.ts), or "Your session has expired — Sign in" when Niobe Desktop reports that
 * Entra rejected the saved session (lib/auth.ts).
 *
 * It replaces the error dialogs that used to stack up after a wake, so it behaves as their opposite.
 * It is a status (role="status"), not an alertdialog. It never takes focus, has nothing to dismiss,
 * and clears by itself when the connection is back. The live region is always in the DOM, so a
 * screen reader announces the message when it appears rather than missing a node inserted
 * already filled.
 *
 * Rendered once at the app root, beside ErrorOverlay and UpdateReadyBanner.
 */
export function ConnectionBanner() {
  const { t } = useLang()
  const connectivity = useConnectivity()
  const expired = useSyncExternalStore(subscribeSessionExpired, isSessionExpired, isSessionExpired)
  const [signingIn, setSigningIn] = useState(false)

  const signIn = (): void => {
    setSigningIn(true)
    // The browser opens now, because the member asked. A cancelled or timed-out sign-in leaves the
    // banner up so they can try again.
    void signInAgain().finally(() => setSigningIn(false))
  }

  // Reconnecting comes first: signing in cannot work until the network is back anyway.
  let content: React.ReactNode = null
  if (connectivity === 'reconnecting') {
    content = (
      <div className="conn-banner">
        <span className="conn-dot" aria-hidden="true" />
        <span>{t('conn_reconnecting')}</span>
      </div>
    )
  } else if (expired) {
    content = (
      <div className="conn-banner">
        <span className="conn-dot" aria-hidden="true" />
        <span>{t('session_expired')}</span>
        <button type="button" disabled={signingIn} onClick={signIn}>
          {signingIn ? t('session_signing_in') : t('session_sign_in')}
        </button>
      </div>
    )
  }

  return createPortal(
    <div role="status" aria-live="polite">
      {content}
    </div>,
    document.body,
  )
}
